import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import { eventService } from '../../Services/EventService'
import PhaseChart, { getMetricData } from './dlm/PhaseChart'
import LatestReadings from './dlm/LatestReadings'
import DlmTimeline from './dlm/DlmTimeline'
import DlmEventLog from './dlm/DlmEventLog'
import { METRICS, TIME_RANGES, deriveStatePeriods } from './dlm/constants'

const INTERVAL_SECONDS = 5
const GAP_THRESHOLD_SECONDS = 15

function fillTimeGaps(docs, intervalSec, gapThresholdSec) {
  if (docs.length < 2) return docs
  const result = []
  for (let i = 0; i < docs.length; i++) {
    result.push(docs[i])
    if (i < docs.length - 1) {
      const currTime = new Date(docs[i].receivedAt).getTime()
      const nextTime = new Date(docs[i + 1].receivedAt).getTime()
      const gapMs = nextTime - currTime
      if (gapMs > gapThresholdSec * 1000) {
        const slots = Math.floor(gapMs / (intervalSec * 1000)) - 1
        for (let slot = 1; slot <= slots; slot++) {
          const syntheticTime = new Date(currTime + slot * intervalSec * 1000).toISOString()
          result.push({ receivedAt: syntheticTime, telemetry: null, _placeholder: true })
        }
      }
    }
  }
  return result
}

function detectPhases(docs) {
  const phasesSeen = new Set()
  for (const doc of docs) {
    const telemetry = doc.telemetry
    if (!telemetry) continue
    for (const key of Object.keys(telemetry)) {
      const arr = telemetry[key]
      if (!Array.isArray(arr)) continue
      for (const item of arr) {
        if (item.channel && item.channel <= 3) phasesSeen.add(item.channel)
      }
    }
  }
  return [...phasesSeen].sort()
}

function computeExpectedCount(timeRange, customFrom, customTo) {
  let durationSec
  if (timeRange.minutes !== null) {
    durationSec = timeRange.minutes * 60
  } else if (customFrom && customTo) {
    durationSec = (new Date(customTo).getTime() - new Date(customFrom).getTime()) / 1000
  } else {
    return null
  }
  return Math.floor(durationSec / INTERVAL_SECONDS)
}

const useStyles = makeStyles((theme) => ({
  dashboard: {
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    background: '#f5f7fa',
    color: '#1f2937',
  },
  dashboardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 24px',
    background: '#00bcd4',
    borderBottom: '1px solid #0097a7',
  },
  headerTitle: {
    fontSize: '18px',
    color: '#ffffff',
    fontWeight: 600,
    margin: 0,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  userName: {
    color: '#ffffff',
    fontSize: '13px',
    opacity: 0.9,
  },
  lastUpdated: {
    color: '#ffffff',
    fontSize: '12px',
    opacity: 0.8,
  },
  autoRefreshToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#ffffff',
    cursor: 'pointer',
    opacity: 0.9,
    '& input': { cursor: 'pointer' },
  },
  controlsBar: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '16px',
    padding: '16px 24px',
    background: '#ffffff',
    borderBottom: '1px solid #e5e7eb',
    flexWrap: 'wrap',
  },
  controlGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    '& label': {
      fontSize: '11px',
      color: '#6b7280',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    '& select, & input[type="datetime-local"]': {
      padding: '8px 12px',
      background: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      color: '#111827',
      fontSize: '13px',
      outline: 'none',
      '&:focus': { borderColor: '#00bcd4' },
    },
  },
  btnPrimary: {
    padding: '8px 20px',
    background: '#00bcd4',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    '&:hover': { background: '#0097a7' },
    '&:disabled': { opacity: 0.6, cursor: 'not-allowed' },
  },
  btnSecondary: {
    padding: '6px 14px',
    background: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
    '&:hover': { background: '#f3f4f6', color: '#111827' },
  },
  metricsSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    flexWrap: 'wrap',
  },
  metricsLabel: {
    fontSize: '12px',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginRight: '4px',
  },
  metricChip: {
    padding: '4px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '16px',
    background: '#ffffff',
    color: '#6b7280',
    fontSize: '12px',
    cursor: 'pointer',
    '&:hover': { borderColor: '#9ca3af', background: '#f9fafb' },
  },
  errorBanner: {
    margin: '12px 24px',
    padding: '10px 16px',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    color: '#dc2626',
    fontSize: '13px',
  },
  infoBanner: {
    margin: '12px 24px',
    padding: '10px 16px',
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '6px',
    color: '#2563eb',
    fontSize: '13px',
  },
  summaryCards: {
    display: 'flex',
    gap: '12px',
    padding: '16px 24px',
    flexWrap: 'wrap',
    background: '#ffffff',
  },
  summaryCard: {
    flex: 1,
    minWidth: '120px',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  summaryLabel: {
    fontSize: '11px',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  summaryValue: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
  },
  dlmBandLegend: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    padding: '10px 24px',
    fontSize: '13px',
    color: '#374151',
  },
  dlmBandLegendLabel: {
    fontWeight: 700,
    color: '#111827',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontSize: '12px',
  },
  dlmBandLegendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontWeight: 500,
  },
  dlmBandSwatch: {
    display: 'inline-block',
    width: '24px',
    height: '14px',
    borderRadius: '3px',
    border: '1px solid rgba(0,0,0,0.2)',
  },
  chartsAndReadings: {
    display: 'flex',
    gap: '20px',
    padding: '20px 24px 24px',
    background: '#f5f7fa',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: { flexDirection: 'column' },
  },
  chartsColumn: {
    flex: 3,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  readingsColumn: {
    flex: '0 0 280px',
    minWidth: 0,
    [theme.breakpoints.down('md')]: { flex: '1 1 auto' },
  },
  noData: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    color: '#9ca3af',
    fontSize: '14px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  collapsibleSection: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    overflow: 'hidden',
  },
  collapsibleSectionBottom: {
    margin: '0 24px 24px',
  },
  collapsibleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    padding: '10px 16px',
    background: '#f9fafb',
    border: 'none',
    borderBottom: '1px solid #e5e7eb',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    textAlign: 'left',
    '&:hover': { background: '#f3f4f6' },
  },
  collapseArrow: {
    fontSize: '10px',
    color: '#6b7280',
    display: 'inline-block',
    transition: 'transform 0.2s',
  },
  collapseArrowOpen: {
    transform: 'rotate(90deg)',
  },
  collapsibleTitle: {
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
}))

const NewDashboard = ({ currentUser }) => {
  const classes = useStyles()
  const [siteIds, setSiteIds] = useState([])
  const [siteId, setSiteId] = useState(null)
  const [timeRange, setTimeRange] = useState(TIME_RANGES[1])
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [selectedMetricKeys, setSelectedMetricKeys] = useState(['proPMw', 'conPMw', 'netBattery'])
  const [telemetryDocs, setTelemetryDocs] = useState([])
  const [phases, setPhases] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [lastFetch, setLastFetch] = useState(null)
  const [pinnedReadingTs, setPinnedReadingTs] = useState(null)
  const [zoomRange, setZoomRange] = useState(null)
  const [hoverInfo, setHoverInfo] = useState(null)
  const [auditLogs, setAuditLogs] = useState([])
  const [collapsedPhases, setCollapsedPhases] = useState({})
  const [eventLogCollapsed, setEventLogCollapsed] = useState(true)
  const intervalRef = useRef(null)
  const slideOffsetMs = useRef(0)
  const [slideTick, setSlideTick] = useState(0)

  const displayName = currentUser?.name || currentUser?.username || currentUser?.email || 'Admin'

  const getTimeWindow = useCallback(() => {
    if (timeRange.minutes === null) {
      const offset = slideOffsetMs.current
      return {
        from: new Date(new Date(customFrom).getTime() + offset),
        to: new Date(new Date(customTo).getTime() + offset),
      }
    }
    const now = new Date()
    const from = new Date(now.getTime() - timeRange.minutes * 60 * 1000)
    return { from, to: now }
  }, [timeRange, customFrom, customTo])

  useEffect(() => {
    eventService.fetchDlmSiteIds()
      .then((ids) => {
        setSiteIds(ids)
        if (ids.length > 0) setSiteId(ids[0])
      })
      .catch((err) => {
        const message = err?.data?.error || err?.message || 'Failed to load site IDs'
        setError(`Site IDs: ${message}`)
      })
  }, [])

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const { from, to } = getTimeWindow()
      const [data, logs] = await Promise.all([
        eventService.fetchDlmTelemetry(siteId, from, to),
        eventService.fetchDlmAuditLogs(siteId, from, to).catch(() => []),
      ])
      setTelemetryDocs(data)
      setPhases(detectPhases(data))
      setAuditLogs(logs)
      setLastFetch(new Date())
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [siteId, getTimeWindow])

  useEffect(() => {
    if (!siteId) return
    setZoomRange(null)
    slideOffsetMs.current = 0
    setSlideTick(0)
    loadData()
  }, [siteId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        slideOffsetMs.current += INTERVAL_SECONDS * 1000
        setSlideTick((tick) => tick + 1)
        if(siteId){
          loadData()
        }
      }, 5000)
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [autoRefresh, loadData])

  const toggleMetric = (key) => {
    setSelectedMetricKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    )
  }

  const selectedMetrics = METRICS.filter((metric) => selectedMetricKeys.includes(metric.key))
  const isSinglePhase = phases.length === 1
  const expectedCount = computeExpectedCount(timeRange, customFrom, customTo)
  const resetZoom = () => { setZoomRange(null); slideOffsetMs.current = 0; setSlideTick(0) }

  const visibleDocs = useMemo(() => {
    if (!zoomRange) return telemetryDocs
    const offsetMs = slideTick * INTERVAL_SECONDS * 1000
    const fromTs = new Date(new Date(zoomRange.fromTs).getTime() + offsetMs).toISOString()
    const toTs = new Date(new Date(zoomRange.toTs).getTime() + offsetMs).toISOString()
    const filtered = telemetryDocs.filter((doc) => doc.receivedAt >= fromTs && doc.receivedAt <= toTs)
    return filtered.length > 0 ? filtered : telemetryDocs
  }, [telemetryDocs, zoomRange, slideTick])

  const expandedDocs = fillTimeGaps(visibleDocs, INTERVAL_SECONDS, GAP_THRESHOLD_SECONDS)

  const handleSelectReadingByIndex = useCallback((index) => {
    if (index === -1) {
      setPinnedReadingTs(null)
    } else if (visibleDocs[index]) {
      setPinnedReadingTs(visibleDocs[index].receivedAt)
    }
  }, [visibleDocs])

  const handleChartSelectReading = useCallback((index) => {
    if (index === -1) {
      setPinnedReadingTs(null)
    } else {
      const doc = expandedDocs[index]
      if (!doc || doc._placeholder) return
      setPinnedReadingTs(doc.receivedAt)
    }
  }, [expandedDocs])

  const handleZoom = useCallback((startIdx, endIdx) => {
    const startTs = expandedDocs[startIdx]?.receivedAt
    const endTs = expandedDocs[endIdx]?.receivedAt
    if (!startTs || !endTs) return
    setZoomRange({ fromTs: startTs, toTs: endTs })
    slideOffsetMs.current = 0
    setSlideTick(0)
  }, [expandedDocs])

  const timeWindow = useMemo(() => {
    if (visibleDocs.length === 0) return null
    const telFrom = new Date(visibleDocs[0].receivedAt)
    const telTo = new Date(visibleDocs[visibleDocs.length - 1].receivedAt)
    const extendedTo = new Date(telTo.getTime() + INTERVAL_SECONDS * 1000)
    return { from: telFrom, to: extendedTo }
  }, [visibleDocs])

  const visibleAuditLogs = useMemo(() => {
    if (!auditLogs || auditLogs.length === 0 || !timeWindow) return []
    const from = timeWindow.from.getTime()
    const to = timeWindow.to.getTime()
    return auditLogs.filter((log) => {
      const timestamp = new Date(log.timestamp).getTime()
      return timestamp >= from && timestamp <= to
    })
  }, [auditLogs, timeWindow])

  const dlmAnnotations = useMemo(() => {
    const sorted = [...visibleAuditLogs].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
    const annotations = []
    const mergedCommandTimestamps = new Set()
    for (let i = 0; i < sorted.length; i++) {
      const log = sorted[i]
      if (log.event_type !== 'STATE_TRANSITION') continue
      let label = `${log.from_state} \u2192 ${log.to_state}`
      if (log.from_state === 'WATCHING' && log.to_state === 'MODULATING') {
        const transTs = new Date(log.timestamp).getTime()
        const paired = sorted.find(
          (event) => event.event_type === 'COMMAND_ISSUED' &&
            Math.abs(new Date(event.timestamp).getTime() - transTs) < 2000
        )
        if (paired) {
          const snap = paired.digital_twin_snapshot
          const cmd = snap?.last_issued_command || snap?.lastIssuedCommand
          const watts = cmd?.discharge_power_watts ?? cmd?.dischargePowerWatts ?? null
          if (watts != null) label += ` ${watts}W`
          mergedCommandTimestamps.add(paired.timestamp)
        }
      }
      annotations.push({
        timestamp: log.timestamp,
        label,
        color: log.to_state === 'MODULATING' ? '#f59e0b' : '#6b7280',
      })
    }
    return { annotations, mergedCommandTimestamps }
  }, [visibleAuditLogs])

  const dlmStatePeriods = useMemo(() => {
    if (!timeWindow || visibleAuditLogs.length === 0) return []
    return deriveStatePeriods(visibleAuditLogs, timeWindow.from, timeWindow.to)
  }, [visibleAuditLogs, timeWindow])

  const dlmCommandEvents = useMemo(() => {
    const merged = dlmAnnotations.mergedCommandTimestamps
    return visibleAuditLogs
      .filter((log) => (log.event_type === 'COMMAND_ISSUED' || log.event_type === 'COMMAND_FAILED') && !merged.has(log.timestamp))
      .map((log) => {
        const snap = log.digital_twin_snapshot
        const cmd = snap?.last_issued_command || snap?.lastIssuedCommand
        const watts = cmd?.discharge_power_watts ?? cmd?.dischargePowerWatts ?? null
        const durationMin = cmd?.command_duration_minutes ?? cmd?.commandDurationMinutes ?? null
        const issuedAt = cmd?.issued_at ?? cmd?.issuedAt ?? null
        const expiresAt = cmd?.expires_at ?? cmd?.expiresAt ?? null
        return {
          timestamp: log.timestamp,
          type: log.event_type,
          watts,
          durationMin,
          issuedAt,
          expiresAt,
          reason: log.reason || '',
          color: log.event_type === 'COMMAND_FAILED' ? '#dc2626' : '#059669',
        }
      })
  }, [visibleAuditLogs, dlmAnnotations])

  const dlmDischargeSteps = useMemo(() => {
    if (!visibleAuditLogs || visibleAuditLogs.length === 0) return []
    const allCommands = visibleAuditLogs
      .filter((log) => log.event_type === 'COMMAND_ISSUED')
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      .map((log) => {
        const snap = log.digital_twin_snapshot
        const cmd = snap?.last_issued_command || snap?.lastIssuedCommand
        const watts = cmd?.discharge_power_watts ?? cmd?.dischargePowerWatts ?? 0
        const expiresAt = cmd?.expires_at ?? cmd?.expiresAt ?? null
        return { timestamp: log.timestamp, watts, expiresAt }
      })
    const steps = []
    for (let i = 0; i < allCommands.length; i++) {
      const cmd = allCommands[i]
      const from = new Date(cmd.timestamp)
      const nextCmd = allCommands[i + 1]
      const expiry = cmd.expiresAt ? new Date(cmd.expiresAt) : null
      let to
      if (nextCmd && expiry) {
        to = new Date(Math.min(new Date(nextCmd.timestamp).getTime(), expiry.getTime()))
      } else if (nextCmd) {
        to = new Date(nextCmd.timestamp)
      } else if (expiry) {
        to = expiry
      } else {
        to = timeWindow ? timeWindow.to : from
      }
      steps.push({ from, to, watts: cmd.watts })
    }
    return steps
  }, [visibleAuditLogs, timeWindow])

  const importLimitWatts = useMemo(() => {
    if (!visibleAuditLogs || visibleAuditLogs.length === 0) return null
    for (let i = visibleAuditLogs.length - 1; i >= 0; i--) {
      const snap = visibleAuditLogs[i].digital_twin_snapshot
      if (!snap) continue
      const dlm = snap.dlm || snap.DLM
      if (!dlm) continue
      const calcWindow = dlm.calculation_window || dlm.calculationWindow
      if (!calcWindow || !Array.isArray(calcWindow) || calcWindow.length === 0) continue
      const latest = calcWindow[calcWindow.length - 1]
      const tEntry = latest.t_entry_mw_per_phase || latest.tEntryMwPerPhase
      if (tEntry && Array.isArray(tEntry) && tEntry.length > 0) {
        return tEntry[0] / 1000
      }
    }
    return null
  }, [visibleAuditLogs])

  const sharedYRange = useMemo(() => {
    if (!expandedDocs || expandedDocs.length === 0 || selectedMetrics.length === 0 || phases.length === 0) return null
    let globalMin = Infinity
    let globalMax = -Infinity
    for (const phase of phases) {
      for (const metric of selectedMetrics) {
        const { data } = getMetricData(expandedDocs, metric, phase)
        for (const value of data) {
          if (value !== null && value !== undefined) {
            if (value < globalMin) globalMin = value
            if (value > globalMax) globalMax = value
          }
        }
      }
    }
    if (importLimitWatts != null) {
      if (importLimitWatts > globalMax) globalMax = importLimitWatts
      if (importLimitWatts < globalMin) globalMin = importLimitWatts
    }
    if (dlmDischargeSteps && dlmDischargeSteps.length > 0 && phases.length > 0) {
      for (const step of dlmDischargeSteps) {
        const perPhase = step.watts / phases.length
        if (perPhase > globalMax) globalMax = perPhase
        if (perPhase < globalMin) globalMin = perPhase
      }
    }
    if (!isFinite(globalMin) || !isFinite(globalMax)) return null
    const range = globalMax - globalMin
    const padding = range * 0.05 || 10
    return { min: globalMin - padding, max: globalMax + padding }
  }, [expandedDocs, selectedMetrics, phases, importLimitWatts, dlmDischargeSteps])

  const togglePhaseCollapse = (phase) => {
    setCollapsedPhases((prev) => ({ ...prev, [phase]: !prev[phase] }))
  }

  return (
    <div className={classes.dashboard}>
      <header className={classes.dashboardHeader}>
        <div>
          <h1 className={classes.headerTitle}>Dynamic Load Management</h1>
        </div>
        <div className={classes.headerRight}>
          <span className={classes.userName}>{displayName}</span>
          {lastFetch && (
            <span className={classes.lastUpdated}>
              Updated: {lastFetch.toLocaleTimeString()}
            </span>
          )}
          <label className={classes.autoRefreshToggle}>
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            Auto Refresh (5s)
          </label>
        </div>
      </header>

      <div className={classes.controlsBar}>
        <div className={classes.controlGroup}>
          <label>Site ID</label>
          <select value={siteId ?? ''} onChange={(e) => setSiteId(Number(e.target.value))} disabled={siteIds.length === 0}>
            {siteIds.length === 0 && <option value=''>Loading...</option>}
            {siteIds.map((id) => (
              <option key={id} value={id}>{id}</option>
            ))}
          </select>
        </div>
        <div className={classes.controlGroup}>
          <label>Time Range</label>
          <select
            value={timeRange.label}
            onChange={(e) => setTimeRange(TIME_RANGES.find((range) => range.label === e.target.value))}
          >
            {TIME_RANGES.map((range) => (
              <option key={range.label} value={range.label}>{range.label}</option>
            ))}
          </select>
        </div>
        {timeRange.minutes === null && (
          <>
            <div className={classes.controlGroup}>
              <label>From</label>
              <input type="datetime-local" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
            </div>
            <div className={classes.controlGroup}>
              <label>To</label>
              <input type="datetime-local" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
            </div>
          </>
        )}
        <button
          className={classes.btnPrimary}
          onClick={() => { setZoomRange(null); slideOffsetMs.current = 0; setSlideTick(0); loadData() }}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Apply'}
        </button>
        {zoomRange && (
          <button className={classes.btnSecondary} onClick={resetZoom}>
            Reset Zoom
          </button>
        )}
      </div>

      <div className={classes.metricsSelector}>
        <span className={classes.metricsLabel}>Metrics:</span>
        {METRICS.map((metric) => (
          <button
            key={metric.key}
            className={classes.metricChip}
            style={selectedMetricKeys.includes(metric.key)
              ? { backgroundColor: metric.color + '30', borderColor: metric.color, color: metric.color, fontWeight: 500 }
              : {}}
            onClick={() => toggleMetric(metric.key)}
          >
            {metric.shortLabel}
          </button>
        ))}
      </div>

      {error && <div className={classes.errorBanner}>{error}</div>}

      {isSinglePhase && (
        <div className={classes.infoBanner}>
          This appears to be a single-phase site (only L{phases[0]} data detected).
        </div>
      )}

      <div className={classes.summaryCards}>
        <div className={classes.summaryCard}>
          <span className={classes.summaryLabel}>Data Points</span>
          <span className={classes.summaryValue}>
            {(() => {
              if (zoomRange && visibleDocs.length >= 2) {
                const startTime = new Date(visibleDocs[0].receivedAt).getTime()
                const endTime = new Date(visibleDocs[visibleDocs.length - 1].receivedAt).getTime()
                const zoomExpected = Math.floor((endTime - startTime) / 1000 / INTERVAL_SECONDS) + 1
                return `${visibleDocs.length} / ${zoomExpected}`
              }
              return `${telemetryDocs.length}${expectedCount !== null ? ` / ${expectedCount}` : ''}`
            })()}
          </span>
        </div>
        <div className={classes.summaryCard}>
          <span className={classes.summaryLabel}>Phases</span>
          <span className={classes.summaryValue}>{phases.map((phase) => `L${phase}`).join(', ') || '-'}</span>
        </div>
        <div className={classes.summaryCard}>
          <span className={classes.summaryLabel}>Site ID</span>
          <span className={classes.summaryValue}>{siteId}</span>
        </div>
        <div className={classes.summaryCard}>
          <span className={classes.summaryLabel}>Interval</span>
          <span className={classes.summaryValue}>{INTERVAL_SECONDS}s</span>
        </div>
        <div className={classes.summaryCard}>
          <span className={classes.summaryLabel}>Battery SOC</span>
          <span className={classes.summaryValue}>
            {(() => {
              if (visibleDocs.length === 0) return '-'
              const lastDoc = visibleDocs[visibleDocs.length - 1]
              const soc = lastDoc.telemetry?.socPer
              if (soc == null) return '-'
              const ts = new Date(lastDoc.receivedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
              return `${soc}% (${ts})`
            })()}
          </span>
        </div>
      </div>

      {visibleAuditLogs.length > 0 && (
        <div className={classes.dlmBandLegend}>
          <span className={classes.dlmBandLegendLabel}>DLM Bands:</span>
          <span className={classes.dlmBandLegendItem}>
            <span className={classes.dlmBandSwatch} style={{ background: 'rgba(239,68,68,0.35)' }} />
            Modulating
          </span>
          <span className={classes.dlmBandLegendItem}>
            <span className={classes.dlmBandSwatch} style={{ background: 'rgba(251,146,60,0.35)' }} />
            Exit Pending
          </span>
          <span className={classes.dlmBandLegendItem}>
            <span className={classes.dlmBandSwatch} style={{ background: 'rgba(229,231,235,0.6)' }} />
            Watching
          </span>
        </div>
      )}

      <div className={classes.chartsAndReadings}>
        <div className={classes.chartsColumn}>
          {phases.map((phase) => {
            const isCollapsed = collapsedPhases[phase] !== false
            return (
              <div key={phase} className={classes.collapsibleSection}>
                <button className={classes.collapsibleHeader} onClick={() => togglePhaseCollapse(phase)}>
                  <span className={`${classes.collapseArrow} ${isCollapsed ? '' : classes.collapseArrowOpen}`}>{'\u25B6'}</span>
                  <span className={classes.collapsibleTitle}>Phase L{phase}</span>
                </button>
                {!isCollapsed && (
                  <PhaseChart
                    telemetryDocs={expandedDocs}
                    selectedMetrics={selectedMetrics}
                    phase={phase}
                    numPhases={phases.length}
                    onSelectReading={handleChartSelectReading}
                    onZoom={handleZoom}
                    hoverInfo={hoverInfo}
                    onHover={setHoverInfo}
                    dlmAnnotations={dlmAnnotations.annotations}
                    dlmStatePeriods={dlmStatePeriods}
                    dlmCommandEvents={dlmCommandEvents}
                    dlmDischargeSteps={dlmDischargeSteps}
                    importLimitWatts={importLimitWatts}
                    sharedYRange={sharedYRange}
                  />
                )}
              </div>
            )
          })}
          {selectedMetrics.length === 0 && (
            <div className={classes.noData}>Select at least one metric to display charts.</div>
          )}
          {telemetryDocs.length === 0 && selectedMetrics.length > 0 && !loading && (
            <div className={classes.noData}>No telemetry data available for the selected time range.</div>
          )}
        </div>
        <div className={classes.readingsColumn}>
          <LatestReadings
            telemetryDocs={visibleDocs}
            phases={phases}
            pinnedReadingTs={pinnedReadingTs}
            onSelectReading={handleSelectReadingByIndex}
          />
        </div>
      </div>

      {timeWindow && visibleAuditLogs.length > 0 && (
        <DlmTimeline
          auditLogs={visibleAuditLogs}
          timeWindowFrom={timeWindow.from}
          timeWindowTo={timeWindow.to}
        />
      )}

      <div className={`${classes.collapsibleSection} ${classes.collapsibleSectionBottom}`}>
        <button className={classes.collapsibleHeader} onClick={() => setEventLogCollapsed((collapsed) => !collapsed)}>
          <span className={`${classes.collapseArrow} ${eventLogCollapsed ? '' : classes.collapseArrowOpen}`}>{'\u25B6'}</span>
          <span className={classes.collapsibleTitle}>DLM Event Log ({visibleAuditLogs.length})</span>
        </button>
        {!eventLogCollapsed && <DlmEventLog auditLogs={visibleAuditLogs} />}
      </div>
    </div>
  )
}

const mapStateToProps = ({ auth }) => {
  const { currentUser, isSuperAdmin, accountId } = auth
  return { currentUser, isSuperAdmin, accountId }
}

export default connect(mapStateToProps)(NewDashboard)